import { useUserInfo } from "@next-platform/client-common-core";
import {
  CapabilityKey,
  SubCapabilityKey,
  useApplicationCapabilitiesAllowed,
  useApplicationCapabilityReady,
} from "@next-platform/client-common-core/infra/application/capabilities";
import { ReactNode } from "react";
import { HelmetProvider } from "react-helmet-async";
import { createBrowserRouter, Navigate, Outlet, redirect, RouterProvider } from "react-router-dom";

import { AppLayout, ProtectedRoutes } from "@/components";
import LazyLoader from "@/components/common/LazyLoader";
import Notification from "@/components/common/Notification";
import RootErrorBoundary from "@/components/common/RootErrorBoundary.jsx";
import PersistentTopBar from "@/components/layouts/AppLayout/PersistentTopBar";
import { EXTERNAL_PERMISSIONS, FEATURE_FLAGS, PERMISSIONS_BAN_TYPES, RELATIVE_ROUTES, ROUTES } from "@/constants";
import { ANDROID_PLATFORM_OS } from "@/features/mobileApplications/constants";
import MobileApplicationsRouteGuard from "@/features/mobileApplications/MobileApplicationsRouteGuard";
import { useFeatureFlag } from "@/hooks";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
// eslint-disable-next-line import/no-namespace -- Aggregated page namespace used pervasively across this router; converting every Pages.* reference is out of scope here.
import * as Pages from "@/pages";
import CapabilityRouteGuard from "@/routes/guards/CapabilityRouteGuard";
import { ExternalAuthProvider } from "@/services/ExternalAuthProvider";

import App from "./app";

const PrivacyPage = LazyLoader(() => import(/* webpackChunkName:"legal" */ "./pages/PrivacyPage"));
const RefundPolicyPage = LazyLoader(() => import(/* webpackChunkName:"legal" */ "./pages/RefundPolicyPage"));
const TermsPage = LazyLoader(() => import(/* webpackChunkName:"legal" */ "./pages/TermsPage"));
const ComplaintPage = LazyLoader(() => import(/* webpackChunkName:"legal" */ "./pages/ComplaintPage"));
const S2257Page = LazyLoader(() => import(/* webpackChunkName:"legal" */ "./pages/S2257Page"));
const ChatStoryPage = LazyLoader(() => import(/* webpackChunkName:"chat-story" */ "./pages/ChatStory/ChatStory"));
const RulebookEditorPage = LazyLoader(() => import(/* webpackChunkName:"rulebook-editor" */ "./pages/RulebookEditor"));
const LoginPage = LazyLoader(() => import(/* webpackChunkName:"login" */ "./pages/Login/Login"));
const CreatorAnalyticsPage = LazyLoader(() => import(/* webpackChunkName:"creator-analytics" */ "./pages/CreatorAnalytics/CreatorAnalyticsPage"));
const CharacterAnalyticsPage = LazyLoader(() => import(/* webpackChunkName:"creator-analytics" */ "./pages/CreatorAnalytics/CharacterAnalyticsPage"));
// The Gallery pages show their own loading state, so the chunk download shows nothing.
const MyGalleryPage = LazyLoader(() => import(/* webpackChunkName:"gallery" */ "./pages/Gallery/MyGallery"), null);
const MediaFilePage = LazyLoader(() => import(/* webpackChunkName:"media-file" */ "./pages/Gallery/MediaFile"), null);

const LOADING_FALLBACK_TEXT = "Loading";

const Fallback = () => {
  return <p>{LOADING_FALLBACK_TEXT}</p>;
};

function VoiceGuard({ children }: { children: ReactNode }) {
  const { isProfileLoaded, isAuthenticated } = useUserInfo();
  const isCapabilitiesReady = useApplicationCapabilityReady();
  const isTtsAllowed = useApplicationCapabilitiesAllowed(CapabilityKey.Tts);

  if (!isAuthenticated) {
    return <>{children}</>;
  }
  if (isAuthenticated && !isProfileLoaded) {
    return null;
  }
  if (!isCapabilitiesReady) {
    return null;
  }
  if (!isTtsAllowed) {
    return <Navigate to={`/`} replace />;
  }
  return <>{children}</>;
}

function CreatorAnalyticsGuard({ children }: { children: ReactNode }) {
  const { hasFeatureFlag: hasCreatorAnalytics, isLoading } = useFeatureFlag(FEATURE_FLAGS.CREATOR_ANALYTICS);

  if (isLoading) {
    return <Fallback />;
  }

  if (!hasCreatorAnalytics) {
    return <Navigate to={RELATIVE_ROUTES.accessDenied} replace />;
  }

  return <>{children}</>;
}

function DiscoverGuard({ children }: { children: ReactNode }) {
  const localizePathname = useLocalizedPathname();

  return (
    <CapabilityRouteGuard capability={CapabilityKey.DiscoveryPage} redirectTo={localizePathname(ROUTES.home)}>
      {children}
    </CapabilityRouteGuard>
  );
}

function createAppRoutes() {
  return [
    {
      element: <PersistentTopBar />,
      children: [
        {
          children: [
            {
              element: <AppLayout hideFooter={true} />,
              children: [
                {
                  path: "chat/:characterId",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Chat />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "chat/:characterId/:conversationId",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Chat />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "story/:characterId",
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.ChatStoryMode}>
                      <ProtectedRoutes
                        element={<ChatStoryPage />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: "story/:characterId/:conversationId",
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.ChatStoryMode}>
                      <ProtectedRoutes
                        element={<ChatStoryPage />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                { path: RELATIVE_ROUTES.externalSignup, element: <Pages.ExternalSignUp /> },
                { path: RELATIVE_ROUTES.accessDenied, element: <Pages.UnauthorizedAccess /> },
              ],
            },
            {
              element: <AppLayout />,
              children: [
                {
                  path: "",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Home />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "external",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Home />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "creator/:username",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Creator />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.blockedCreators,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.DiscoverBlockCreators}>
                      <ProtectedRoutes
                        element={<Pages.BlockedCreators />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.chats,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Chats />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.lorebooksExplore,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.LorebookPublicDiscovery} allowAnonymous>
                      <ProtectedRoutes
                        element={<Pages.LorebooksExplore />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.rulebooksExplore,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.RulebookPublicDiscovery} allowAnonymous>
                      <ProtectedRoutes
                        element={<Pages.RulebooksExplore />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.rulebookEditor,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.RulebookManage}>
                      <ProtectedRoutes
                        element={<RulebookEditorPage />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.rulebookEditorDraft,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.RulebookManage}>
                      <ProtectedRoutes
                        element={<RulebookEditorPage />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.discover,
                  element: (
                    <DiscoverGuard>
                      <ProtectedRoutes
                        element={<Pages.Discover />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </DiscoverGuard>
                  ),
                },
                {
                  path: "chats/:characterId",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.ChatsChatbot />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "chatbot/:characterId",
                  element: <Pages.ChatbotProfile />,
                },
                {
                  path: RELATIVE_ROUTES.chatbotTag,
                  element: <Pages.ChatbotTag />,
                },
                {
                  path: RELATIVE_ROUTES.groupchatProfile,
                  element: <Pages.GroupchatProfile />,
                },
                { path: RELATIVE_ROUTES.login, element: <LoginPage /> },
                { path: RELATIVE_ROUTES.privacy, element: <PrivacyPage /> },
                { path: RELATIVE_ROUTES.terms, element: <TermsPage /> },
                { path: RELATIVE_ROUTES.complaint, element: <ComplaintPage /> },
                { path: RELATIVE_ROUTES.refund, element: <RefundPolicyPage /> },
                { path: RELATIVE_ROUTES.compliance, element: <S2257Page /> },
                {
                  path: "plan-comparison/:frequency",
                  element: <Pages.PlanComparison />,
                },
                { path: "subscribe-crypto", element: <Pages.Subscribe /> },
                {
                  path: RELATIVE_ROUTES.welcomeSubscribeStar,
                  element: <Pages.WelcomeSubscribeStar />,
                },
                {
                  path: RELATIVE_ROUTES.leaderboards,
                  element: <Pages.CreatorsLeaderboard />,
                },
                {
                  path: RELATIVE_ROUTES.download,
                  element: (
                    <MobileApplicationsRouteGuard>
                      <Pages.SupportedApplications />
                    </MobileApplicationsRouteGuard>
                  ),
                },
                { path: "*", element: <Pages.NotFound /> },
                {
                  path: RELATIVE_ROUTES.report,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Report />}
                      permission={EXTERNAL_PERMISSIONS.report_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.recommendedBots,
                  element: <Pages.ChatbotRecommended />,
                },
                {
                  path: RELATIVE_ROUTES.recommendedBots,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.ChatbotRecommended />}
                      permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "",
                  element: <Outlet />,
                  errorElement: <RootErrorBoundary />,
                  children: [],
                },
              ],
            },
            {
              element: <AppLayout isProtected={true} />,
              children: [
                {
                  path: RELATIVE_ROUTES.creatorAnalytics,
                  element: (
                    <ProtectedRoutes
                      element={
                        <CapabilityRouteGuard capability={CapabilityKey.CreatorAnalytics}>
                          <CreatorAnalyticsGuard>
                            <CreatorAnalyticsPage />
                          </CreatorAnalyticsGuard>
                        </CapabilityRouteGuard>
                      }
                      permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: `${RELATIVE_ROUTES.creatorAnalytics}/:charId`,
                  element: (
                    <ProtectedRoutes
                      element={
                        <CapabilityRouteGuard capability={CapabilityKey.CreatorAnalytics}>
                          <CreatorAnalyticsGuard>
                            <CharacterAnalyticsPage />
                          </CreatorAnalyticsGuard>
                        </CapabilityRouteGuard>
                      }
                      permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.myGallery,
                  element: (
                    <ProtectedRoutes
                      element={
                        <CapabilityRouteGuard capability={SubCapabilityKey.GalleryMyGallery} allowAnonymous>
                          <MyGalleryPage />
                        </CapabilityRouteGuard>
                      }
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.mediaFile,
                  element: (
                    <ProtectedRoutes
                      element={
                        <CapabilityRouteGuard capability={SubCapabilityKey.MediaMediaPage} allowAnonymous>
                          <MediaFilePage />
                        </CapabilityRouteGuard>
                      }
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                { path: RELATIVE_ROUTES.signUp, element: <Pages.SignUp /> },
                { path: RELATIVE_ROUTES.subscribe, element: <Pages.Subscribe /> },
                {
                  path: RELATIVE_ROUTES.manageSubscription,
                  element: <Pages.SubscriptionPortal />,
                },
                { path: RELATIVE_ROUTES.portal, element: <Pages.SubscriptionPortal /> },
                { path: RELATIVE_ROUTES.checkout, element: <Pages.Checkout /> },
                { path: RELATIVE_ROUTES.checkoutSuccess, element: <Pages.CheckoutSuccess /> },
                { path: RELATIVE_ROUTES.updateEmail, element: <Pages.UpdateEmail /> },
                { path: RELATIVE_ROUTES.profile, element: <Pages.UserProfile /> },
                { path: RELATIVE_ROUTES.userNotFound, element: <Pages.UserNotFound /> },
                {
                  path: "my-characters",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.ChatbotMy />}
                      permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                // DO NOT CHANGE -> Redirect old /my-chatbots URL to /chatbot/create (public-facing page)
                { path: "my-chatbots", loader: () => redirect(`../${RELATIVE_ROUTES.createChatbot}`) },
                {
                  path: "my-creations",
                  element: <Pages.CreationLayout />,
                  children: [
                    {
                      index: true,
                      loader: () => redirect(RELATIVE_ROUTES.chatbots),
                    },
                    {
                      path: "chatbots",
                      element: (
                        <ProtectedRoutes
                          element={<Pages.ChatbotMy />}
                          permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                          fallbackPath={RELATIVE_ROUTES.accessDenied}
                        />
                      ),
                    },
                    {
                      path: "lorebooks",
                      element: (
                        <CapabilityRouteGuard capability={SubCapabilityKey.LorebookManage}>
                          <ProtectedRoutes
                            element={<Pages.LorebookMy />}
                            permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                            fallbackPath={RELATIVE_ROUTES.accessDenied}
                          />
                        </CapabilityRouteGuard>
                      ),
                    },
                    {
                      path: "rulebooks",
                      element: (
                        <CapabilityRouteGuard capability={SubCapabilityKey.RulebookManage}>
                          <ProtectedRoutes
                            element={<Pages.RulebookMy />}
                            permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                            fallbackPath={RELATIVE_ROUTES.accessDenied}
                          />
                        </CapabilityRouteGuard>
                      ),
                    },
                    {
                      path: "groups",
                      element: (
                        <CapabilityRouteGuard capability={SubCapabilityKey.GroupChatManage}>
                          <ProtectedRoutes
                            element={<Pages.GroupChatsMy />}
                            permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                            fallbackPath={RELATIVE_ROUTES.accessDenied}
                          />
                        </CapabilityRouteGuard>
                      ),
                    },
                    {
                      path: "voices",
                      element: (
                        <CapabilityRouteGuard capability={SubCapabilityKey.TtsManageCustomVoice}>
                          <VoiceGuard>
                            <Pages.MyVoices />
                          </VoiceGuard>
                        </CapabilityRouteGuard>
                      ),
                    },
                  ],
                },
                {
                  path: RELATIVE_ROUTES.chatbots,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.ChatbotMy />}
                      permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.myCreationLorebook,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.LorebookManage}>
                      <ProtectedRoutes
                        element={<Pages.LorebookMy />}
                        permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.myCreationRulebook,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.RulebookManage}>
                      <ProtectedRoutes
                        element={<Pages.RulebookMy />}
                        permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.groupchats,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.GroupChatManage}>
                      <ProtectedRoutes
                        element={<Pages.GroupChatsMy />}
                        permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.favoriteBots,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.ChatbotFavorite />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.favoritesLorebooks,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.FavoritesLorebooks />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.favoritesGroups,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.FavoritesGroups />}
                      permission={EXTERNAL_PERMISSIONS.view_chats}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.characterCreate,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Chatbot />}
                      permission={EXTERNAL_PERMISSIONS.create_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.chatbotCreateId,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Chatbot />}
                      permission={EXTERNAL_PERMISSIONS.create_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "character/edit/:characterId",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Chatbot />}
                      permission={EXTERNAL_PERMISSIONS.create_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: "chatbot/edit/:characterId",
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Chatbot />}
                      permission={EXTERNAL_PERMISSIONS.create_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.linkSubscribeStarId,
                  element: <Pages.LinkSubscribeStar />,
                },
                {
                  path: RELATIVE_ROUTES.linkSubscribeStar,
                  element: <Pages.LinkSubscribeStar />,
                },
                {
                  path: RELATIVE_ROUTES.reportLorebookId,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.ReportLorebook />}
                      permission={EXTERNAL_PERMISSIONS.report_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.reportRulebookId,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.ReportRulebook />}
                      permission={EXTERNAL_PERMISSIONS.report_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                {
                  path: RELATIVE_ROUTES.rulebookDetail,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.RulebookPublicDiscovery} allowAnonymous>
                      <ProtectedRoutes
                        element={<Pages.RulebookProfile />}
                        permission={EXTERNAL_PERMISSIONS.view_chats}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.reportChatbot,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.Report />}
                      permission={EXTERNAL_PERMISSIONS.report_character}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
                { path: RELATIVE_ROUTES.welcome, element: <Pages.Welcome /> },
              ],
            },
          ],
        },
        {
          element: <AppLayout />,
          children: [
            {
              path: "",
              element: (
                <ProtectedRoutes element={<Pages.Home />} permission={EXTERNAL_PERMISSIONS.view_chats} fallbackPath={RELATIVE_ROUTES.accessDenied} />
              ),
            },
            {
              path: "external",
              element: (
                <ProtectedRoutes element={<Pages.Home />} permission={EXTERNAL_PERMISSIONS.view_chats} fallbackPath={RELATIVE_ROUTES.accessDenied} />
              ),
            },
            {
              path: "creator/:username",
              element: (
                <ProtectedRoutes
                  element={<Pages.Creator />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.chats,
              element: (
                <ProtectedRoutes element={<Pages.Chats />} permission={EXTERNAL_PERMISSIONS.view_chats} fallbackPath={RELATIVE_ROUTES.accessDenied} />
              ),
            },
            {
              path: "chats/:characterId",
              element: (
                <ProtectedRoutes
                  element={<Pages.ChatsChatbot />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            { path: RELATIVE_ROUTES.login, element: <LoginPage /> },
            { path: "privacy", element: <PrivacyPage /> },
            { path: "terms", element: <TermsPage /> },
            {
              path: RELATIVE_ROUTES.chatbotTag,
              element: <Pages.ChatbotTag />,
            },
            {
              path: RELATIVE_ROUTES.welcomeSubscribeStar,
              element: <Pages.WelcomeSubscribeStar />,
            },
            { path: "*", element: <Pages.NotFound /> },
            {
              path: "",
              element: <Outlet />,
              errorElement: <RootErrorBoundary />,
              children: [],
            },
            {
              path: RELATIVE_ROUTES.androidInstallation,
              element: (
                <MobileApplicationsRouteGuard requiredType={ANDROID_PLATFORM_OS}>
                  <Pages.AndroidInstallation />
                </MobileApplicationsRouteGuard>
              ),
            },
          ],
        },
        {
          element: <AppLayout />,
          children: [{ path: RELATIVE_ROUTES.getAccess, element: <Pages.SubscribeLanding /> }],
        },
        {
          element: <AppLayout isProtected={true} />,
          children: [
            { path: RELATIVE_ROUTES.signUp, element: <Pages.SignUp /> },
            { path: RELATIVE_ROUTES.updateEmail, element: <Pages.UpdateEmail /> },
            { path: RELATIVE_ROUTES.profile, element: <Pages.UserProfile /> },
            { path: RELATIVE_ROUTES.userNotFound, element: <Pages.UserNotFound /> },
            {
              path: RELATIVE_ROUTES.lorebookCreate,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.LorebookManage}>
                  <Pages.Lorebook />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.lorebookEdit,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.LorebookManage}>
                  <Pages.Lorebook />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.lorebookEditEntries,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.LorebookManage}>
                  <Pages.Lorebook />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.lorebookDetail,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.LorebookDetail}>
                  <Pages.Lorebook />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.myCreationLorebook,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.LorebookManage}>
                  <Pages.LorebookMy />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.myCreationRulebook,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.RulebookManage}>
                  <Pages.RulebookMy />
                </CapabilityRouteGuard>
              ),
            },

            {
              element: <Pages.GroupChatLayout />,
              children: [
                {
                  path: RELATIVE_ROUTES.groupchatCreate,
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.GroupChatManage}>
                      <ProtectedRoutes
                        element={<Pages.Groupchat />}
                        permission={EXTERNAL_PERMISSIONS.create_character}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: "group/edit/:characterId",
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.GroupChatManage}>
                      <ProtectedRoutes
                        element={<Pages.GroupchatEdit />}
                        permission={EXTERNAL_PERMISSIONS.create_character}
                        fallbackPath={RELATIVE_ROUTES.accessDenied}
                      />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: "group/create/:draftId",
                  element: (
                    <CapabilityRouteGuard capability={SubCapabilityKey.GroupChatManage}>
                      <Pages.Groupchat />
                    </CapabilityRouteGuard>
                  ),
                },
                {
                  path: RELATIVE_ROUTES.groupChatCreateMembers,
                  element: (
                    <ProtectedRoutes
                      element={<Pages.GroupChatMembers />}
                      permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                      fallbackPath={RELATIVE_ROUTES.accessDenied}
                    />
                  ),
                },
              ],
            },

            {
              path: "my-characters",
              element: (
                <ProtectedRoutes
                  element={<Pages.ChatbotMy />}
                  permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            // DO NOT CHANGE -> Redirect old /my-chatbots URL to /chatbot/create (public-facing page).
            { path: "my-chatbots", loader: () => redirect(`../${RELATIVE_ROUTES.createChatbot}`) },
            { path: "my-creations", element: <Pages.CreationLayout /> },

            {
              path: RELATIVE_ROUTES.chatbots,
              element: (
                <ProtectedRoutes
                  element={<Pages.ChatbotMy />}
                  permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.chatbotsDrafts,
              element: (
                <ProtectedRoutes
                  element={<Pages.Draft />}
                  permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.groupchatsDrafts,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.GroupChatManage}>
                  <ProtectedRoutes
                    element={<Pages.GroupChatDraft />}
                    permission={EXTERNAL_PERMISSIONS.view_my_chat_bots}
                    fallbackPath={RELATIVE_ROUTES.accessDenied}
                  />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.personas,
              element: (
                <ProtectedRoutes
                  element={<Pages.Personas />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.voices,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.TtsManageCustomVoice}>
                  <ProtectedRoutes
                    element={
                      <VoiceGuard>
                        <Pages.MyVoices />
                      </VoiceGuard>
                    }
                    permission={EXTERNAL_PERMISSIONS.view_chats}
                    fallbackPath={RELATIVE_ROUTES.accessDenied}
                  />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.voiceCreate,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.TtsManageCustomVoice}>
                  <ProtectedRoutes
                    element={
                      <VoiceGuard>
                        <Pages.VoiceCreate />
                      </VoiceGuard>
                    }
                    permission={EXTERNAL_PERMISSIONS.view_chats}
                    fallbackPath={RELATIVE_ROUTES.accessDenied}
                  />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: "voice/:voiceId",
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.TtsManageCustomVoice}>
                  <ProtectedRoutes
                    element={<Pages.VoiceDetail />}
                    permission={EXTERNAL_PERMISSIONS.view_chats}
                    fallbackPath={RELATIVE_ROUTES.accessDenied}
                  />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.voiceClonePreview,
              element: (
                <ProtectedRoutes
                  element={<Pages.VoiceClonePreview />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.voiceCloneError,
              element: (
                <ProtectedRoutes
                  element={<Pages.VoiceCloneError />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.createPersona,
              element: (
                <ProtectedRoutes
                  element={<Pages.PersonaCreate />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: "personas/edit/:personaId",
              element: (
                <ProtectedRoutes
                  element={<Pages.PersonaEdit />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.favoriteBots,
              element: (
                <ProtectedRoutes
                  element={<Pages.ChatbotFavorite />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.favoritesLorebooks,
              element: (
                <ProtectedRoutes
                  element={<Pages.FavoritesLorebooks />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.favoritesGroups,
              element: (
                <ProtectedRoutes
                  element={<Pages.FavoritesGroups />}
                  permission={EXTERNAL_PERMISSIONS.view_chats}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.characterCreate,
              element: (
                <ProtectedRoutes
                  element={<Pages.Chatbot />}
                  permission={EXTERNAL_PERMISSIONS.create_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.createChatbot,
              element: (
                <ProtectedRoutes
                  element={<Pages.Chatbot />}
                  permission={EXTERNAL_PERMISSIONS.create_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: "character/edit/:characterId",
              element: (
                <ProtectedRoutes
                  element={<Pages.Chatbot />}
                  permission={EXTERNAL_PERMISSIONS.create_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: "chatbot/edit/:characterId",
              element: (
                <ProtectedRoutes
                  element={<Pages.Chatbot />}
                  permission={EXTERNAL_PERMISSIONS.create_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.linkSubscribeStarId,
              element: <Pages.LinkSubscribeStar />,
            },
            { path: RELATIVE_ROUTES.linkSubscribeStar, element: <Pages.LinkSubscribeStar /> },
            {
              path: RELATIVE_ROUTES.report,
              element: (
                <ProtectedRoutes
                  element={<Pages.Report />}
                  permission={EXTERNAL_PERMISSIONS.report_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.reportLorebookId,
              element: (
                <ProtectedRoutes
                  element={<Pages.ReportLorebook />}
                  permission={EXTERNAL_PERMISSIONS.report_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.reportRulebookId,
              element: (
                <ProtectedRoutes
                  element={<Pages.ReportRulebook />}
                  permission={EXTERNAL_PERMISSIONS.report_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            {
              path: RELATIVE_ROUTES.rulebookDetail,
              element: (
                <CapabilityRouteGuard capability={SubCapabilityKey.RulebookPublicDiscovery} allowAnonymous>
                  <ProtectedRoutes
                    element={<Pages.RulebookProfile />}
                    permission={EXTERNAL_PERMISSIONS.view_chats}
                    fallbackPath={RELATIVE_ROUTES.accessDenied}
                  />
                </CapabilityRouteGuard>
              ),
            },
            {
              path: RELATIVE_ROUTES.reportChatbot,
              element: (
                <ProtectedRoutes
                  element={<Pages.Report />}
                  permission={EXTERNAL_PERMISSIONS.report_character}
                  fallbackPath={RELATIVE_ROUTES.accessDenied}
                />
              ),
            },
            { path: RELATIVE_ROUTES.welcome, element: <Pages.Welcome /> },
            { path: RELATIVE_ROUTES.ageVerification, element: <Pages.AgeVerification /> },
            { path: RELATIVE_ROUTES.ageVerificationCallback, element: <Pages.AgeVerificationCallback /> },
            { path: RELATIVE_ROUTES.ageVerificationFallback, element: <Pages.AgeVerificationFallback /> },
          ],
        },
      ],
    },
  ];
}

const router = createBrowserRouter([
  {
    path: "/*",
    element: <App />,
    errorElement: <RootErrorBoundary />,
    children: createAppRoutes(),
  },
  {
    path: "/:lang",
    element: <App />,
    errorElement: <RootErrorBoundary />,
    children: createAppRoutes(),
  },
  { path: "*", element: <Pages.NotFound /> },
]);

const bannedChildren = [
  { path: "", element: <Pages.Banned /> },
  { path: "*", element: <Pages.Banned /> },
];

const bannedRouter = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <RootErrorBoundary />,
    children: bannedChildren,
  },
  {
    path: "/:lang",
    element: <App />,
    errorElement: <RootErrorBoundary />,
    children: bannedChildren,
  },
]);

if (import.meta.hot) {
  import.meta.hot.dispose(() => router.dispose());
  import.meta.hot.dispose(() => bannedRouter.dispose());
}

const Routes = () => {
  const { permissions } = useUserInfo();

  const newRoute =
    permissions?.includes(PERMISSIONS_BAN_TYPES.restriction_complete) || permissions?.includes(PERMISSIONS_BAN_TYPES.restriction_temporary)
      ? bannedRouter
      : router;

  return (
    <ExternalAuthProvider>
      <Notification />
      <HelmetProvider>
        <RouterProvider router={newRoute} fallbackElement={<Fallback />} />
      </HelmetProvider>
    </ExternalAuthProvider>
  );
};

export default Routes;
